import { Injectable } from '@angular/core';
import { RESPONSE_MESSAGES } from '@config';
import { BehaviorSubject, ObservableInput, Subject } from 'rxjs';
import { catchError, filter, finalize, map, takeUntil, tap } from 'rxjs/operators';
import { v4 as uuid } from 'uuid';

/*
  A global message service

  implemented as singleton for the whole application
  - emits messages on a global message bus

  WARNING: this is only designed for one content-selector and child listeners
  per page / iframe

*/
export interface MessageBusReponse<T> {
  type: string;
  recipients: string[];
  message?: T;
  referenceID?: string;
}

@Injectable({
  providedIn: 'root',
})
export class MessageBus {
  private messageSubject = new BehaviorSubject<MessageBusReponse<any>>({ type: 'init', recipients: [] });
  // private messageSubject = new Subject<{
  //   type: string;
  //   recipients: Array<string>;
  //   message?: any;
  //   referenceID?: string;
  // }>();

  private messageObservable = this.messageSubject.asObservable();

  /**
   * For  Responses -> components/pages shall use this
   * Listening on the BUS
   * you get the message when
   * - type has to be the same
   * - recipient - you either are in the recipient list or you have CATCH_ALL then you get it anyway
   * - you have a reference id then you get it if it is the same as the requested message
   *   if you dont listen on a reference id you get all of them
   */
  public on = (type: string, recipient: string, callback: any, until: any, referenceID?: string) => {
    const instanceId = uuid();
    const checkType = (msg: any) => {
      if (msg.type !== type) return;
      if (!msg.recipients.includes(recipient) && recipient !== 'CATCH_ALL') return;
      if (referenceID && referenceID !== msg.referenceID) return;
      // console.log('[MessageBus]', { referenceID: msg.referenceID, message: msg.message }, { instanceId, type, recipient });
      callback(msg.message, msg.referenceID);
    };
    this.messageObservable
      .pipe(
        takeUntil(until),
        // tap((data) => {
        //   const { type, recipients, message, referenceID } = data;
        //   console.log('[MessageBus] Receive', {
        //     type,
        //     recipients,
        //     message,
        //     referenceID,
        //   });
        // }),
      )
      .subscribe(
        checkType,
        (error) => {
          console.log('[MessageBus]', 'Subscription Error', {
            instanceId,
            type,
            recipient,
            error,
          });
        },
        () => {
          // console.log('[MessageBus]', 'Subscription complete', { instanceId, type, recipient });
        },
      );
  };

  public listen<T>(types: string[], recipient?: string, referenceId?: string) {
    const checkType = (msg: MessageBusReponse<T>) => {
      if (!types.includes(msg.type)) return false;
      if (recipient && !msg.recipients.includes(recipient) &&  recipient !== 'CATCH_ALL') return false;
      if (referenceId && referenceId !== msg.referenceID) return false;
      return true;
    };

    return this.messageObservable.pipe(
      filter((data) => checkType(data)),
      // map((data) => ({...data.message as T})),
      map((data) => data.message as T),
      // finalize(() => console.log('[MessageBus][Listen][Unsubscribe]', types, recipient)),
    );
  }

  public listenToSlug<T extends {slug: string}>(slug: string) {
    const checkType = (msg: MessageBusReponse<T>) => {
      const types: string[] = [RESPONSE_MESSAGES.ContentBySlug, RESPONSE_MESSAGES.ContentUpdate, RESPONSE_MESSAGES.ContentPurge]
      if (!types.includes(msg.type)) return false;
      if (!msg.message.slug || msg.message.slug !== slug) return false;
      return true;
    };

    return this.messageObservable.pipe(
      filter((data) => checkType(data)),
      // map((data) => ({...data.message as T})),
      map((data) => data.message as T),
      finalize(() => console.log('[MessageBus][ListenToSlug][Unsubscribe]', slug)),
    );
  }

  /**
   * processes all requests
   * ONLY Managers should use this
   */
  public process = (type: string, callback: any, until: any) => {
    const checkType = (msg: any) => {
      if (msg.type === type) callback(msg.message, msg.recipients, msg.referenceID);
    };

    this.messageObservable.pipe(takeUntil(until)).subscribe(checkType);
  };

  /*
    Sending Requests
   */
  public emit = (type: string, recipients: Array<string>, message?: any, referenceID?: string) => {
    // console.log('[MessageBus] Emit', {
    //   type: type,
    //   recipients: recipients,
    //   message: message,
    //   referenceID: referenceID,
    // });
    this.messageSubject.next({
      type: type,
      recipients: recipients,
      message: message,
      referenceID: referenceID,
    });
  };
}
